import { useTranslation } from '@/i18n';
import { useNavigate } from 'react-router-dom';
import { smoothScrollTo } from './scrollUtils';

export function HeroSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const scrollToHowItWorks = () => {
    smoothScrollTo('how-it-works');
  };

  return (
    <section className="relative overflow-hidden pt-24 pb-16 md:pt-32 md:pb-24">
      {/* Gradient orbs */}
      <div className="landing-orb landing-orb-1" aria-hidden="true" />
      <div className="landing-orb landing-orb-2" aria-hidden="true" />
      <div className="landing-orb landing-orb-3" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Text */}
          <div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight tracking-tight">
              {t('landing.hero.headline')}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-gray-600 max-w-xl leading-relaxed">
              {t('landing.hero.subtitle')}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => void navigate('/merchant/login')}
                className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-white rounded-lg bg-gradient-to-r from-sky-500 to-indigo-500 hover:from-sky-600 hover:to-indigo-600 shadow-lg shadow-sky-500/25 transition-all min-h-[44px] cursor-pointer"
              >
                {t('landing.hero.ctaPrimary')}
              </button>
              <button
                onClick={scrollToHowItWorks}
                className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50 transition-all min-h-[44px] cursor-pointer"
              >
                {t('landing.hero.ctaSecondary')}
              </button>
            </div>
          </div>

          {/* Illustration - stylized phone + dashboard mockup */}
          <div className="flex justify-center lg:justify-end">
            <div className="relative w-full max-w-md">
              {/* Dashboard card */}
              <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                {/* Stats row */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-sky-50 rounded-lg p-3 text-center">
                    <div className="text-lg font-bold text-sky-600">247</div>
                    <div className="text-xs text-gray-500">{t('landing.hero.mockShares')}</div>
                  </div>
                  <div className="bg-indigo-50 rounded-lg p-3 text-center">
                    <div className="text-lg font-bold text-indigo-600">89</div>
                    <div className="text-xs text-gray-500">{t('landing.hero.mockClaims')}</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <div className="text-lg font-bold text-green-600">{t('landing.hero.mockRevenueValue')}</div>
                    <div className="text-xs text-gray-500">{t('landing.hero.mockRevenue')}</div>
                  </div>
                </div>
                {/* Chart placeholder */}
                <div className="bg-gray-50 rounded-lg h-28 flex items-end justify-between px-4 pb-3 gap-2">
                  {[40, 65, 45, 80, 55, 90, 70].map((h, i) => (
                    <div
                      key={i}
                      className="flex-1 rounded-t bg-gradient-to-t from-sky-500 to-indigo-400 opacity-80"
                      style={{ height: `${h}%` }}
                    />
                  ))}
                </div>
              </div>

              {/* Floating phone card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 border border-gray-100 w-44">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-400 to-indigo-500 flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                    </svg>
                  </div>
                  <span className="text-xs font-medium text-gray-700">{t('landing.hero.mockCoupon')}</span>
                </div>
                <div className="text-sm font-bold text-green-600">{t('landing.hero.mockDiscount')}</div>
                <div className="text-xs text-gray-400 mt-1">{t('landing.hero.mockShared')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
